import { Deal, GstTreatment } from '../types'

/**
 * Division 66 — second-hand goods — in one place.
 *
 * Three things were wrong with the way the register used to work, and all three
 * come from the same root: it never asked what kind of seller the vehicle came
 * from. Every deal was given a notional credit of one eleventh of the purchase
 * price, in the year it was bought, with no ceiling.
 *
 *   1. A vehicle bought from a GST-registered dealer on a tax invoice is not a
 *      margin-scheme purchase at all. The credit is the GST actually shown on
 *      that invoice, and it belongs to the period of purchase.
 *
 *   2. Where the goods cost more than $300, the notional credit on a purchase
 *      from an unregistered seller is not claimable until the goods are SOLD,
 *      and it is limited to the GST payable on that sale. Claiming it up front,
 *      uncapped, overstates the credit on anything still in stock and on
 *      anything sold at a loss.
 *
 *   3. Some acquisitions carry no credit at all.
 *
 * Everything downstream — the register, the month-end report, landed cost —
 * should call into here rather than dividing by eleven on its own.
 */

export const GST_DIVISOR = 11

export const GST_TREATMENT_LABEL: Record<GstTreatment, string> = {
  tax_invoice: 'Tax Invoice Provided',
  notional_itc: 'Non-registered — Notional ITC',
  no_gst: 'No GST',
}

export const GST_TREATMENT_HELP: Record<GstTreatment, string> = {
  tax_invoice:
    'The seller is GST-registered and supplied a tax invoice. The full GST on that invoice is claimable, in the period of purchase.',
  notional_itc:
    'A private, unregistered seller. Division 66 gives a notional credit of one eleventh of the purchase price — claimable only when the vehicle is sold, and capped at the GST charged on that sale.',
  no_gst: 'No credit arises on this acquisition.',
}

export const GST_TREATMENT_OPTIONS: { value: GstTreatment; label: string }[] = [
  { value: 'tax_invoice', label: GST_TREATMENT_LABEL.tax_invoice },
  { value: 'notional_itc', label: GST_TREATMENT_LABEL.notional_itc },
  { value: 'no_gst', label: GST_TREATMENT_LABEL.no_gst },
]

/** One eleventh, to the cent. */
export const gstComponent = (grossAmount: number): number =>
  Math.round((Number(grossAmount) || 0) / GST_DIVISOR * 100) / 100

/**
 * What a deal's treatment is.
 *
 * Falls back to the seller type for rows that predate the column, which is the
 * same rule migration 023 used to backfill — so a screen and the database agree
 * even if the migration has not been run yet.
 */
/**
 * Seller types we expect to be GST registered and to issue a tax invoice.
 *
 * Written as the list that DOES rather than a test for the ones that do not,
 * on purpose: a seller type added to the picker without being considered here
 * falls through to `notional_itc`, which under-claims rather than over-claims.
 * Over-claiming is the mistake that costs money at audit.
 *
 * `auction` and `tender` were added for Winnie's review of the sourcing screen.
 * Both are normally registered entities, but the inference is only ever a
 * default — `deal.gst_treatment`, set explicitly on the deal, still wins.
 */
/*
 * Nick, 5 Sep: company removed from this list.
 *
 * The margin scheme turns on whether the seller is registered for GST, not on
 * what kind of entity they are. A dealer, an auction house and a tender seller
 * must issue a tax invoice - that invoice IS the contract for those types - so
 * they stay. A company need not be registered, and Winnie's own note on the
 * Reports tab puts "private company not registered for GST" inside the margin
 * scheme. Assuming a company was registered handed it a full input tax credit,
 * which over-claims; it now falls through to notional_itc and under-claims.
 *
 * This is only the fallback. Where the ABN has been looked up, the ABR answer
 * has already set gst_treatment directly, and an explicitly set treatment still
 * wins over anything inferred here.
 */
const REGISTERED_SELLER_TYPES = ['dealer', 'auction', 'tender'] as const
type RegisteredSellerType = (typeof REGISTERED_SELLER_TYPES)[number]

export function treatmentOf(deal: Pick<Deal, 'gst_treatment' | 'seller_type'>): GstTreatment {
  if (deal.gst_treatment) return deal.gst_treatment
  return REGISTERED_SELLER_TYPES.includes(deal.seller_type as RegisteredSellerType)
    ? 'tax_invoice'
    : 'notional_itc'
}

export interface CreditAssessment {
  treatment: GstTreatment
  /** The credit before any cap — what the old register reported. */
  notional: number
  /** What can actually be claimed. */
  claimable: number
  /**
   * "Margin Scheme Credit" - the gross amount the notional credit is worked out
   * from. Purchase price on a tax invoice; nil while a margin-scheme car is in
   * stock; and once it sells, the lesser of purchase and sale, which is the ATO's
   * cap. `claimable` is always exactly one eleventh of this, so the register
   * reads across.
   */
  marginSchemeCredit: number
  /**
   * "G1 (BAS)" - Total Sales, the figure that goes on the real BAS label G1. The
   * full, uncapped sale price of a sold vehicle, whatever its GST treatment, and
   * null while it is still in stock.
   *
   * It is a reporting figure only. It is never an input to `claimable`,
   * `notional` or the net GST - wiring it into any of those overstates the
   * credit on every car sold above its purchase price.
   */
  g1Bas: number | null
  /** GST payable on the sale, where there is one. */
  gstOnSale: number | null
  /** True when the cap bit, i.e. the sale did not carry enough GST to cover the credit. */
  capped: boolean
  /**
   * Which period the credit belongs to: 'purchase' for a tax-invoice
   * acquisition, 'sale' for a margin-scheme one. Null when it cannot yet be
   * attributed because the vehicle has not sold.
   */
  attributesTo: 'purchase' | 'sale' | null
  /** The date the credit is attributable to, or null if it is not yet claimable. */
  attributionDate: string | null
  /** Plain-English reason, for the register's notes column. */
  note: string
}

export interface SaleFacts {
  /** Gross, GST-inclusive sale price. Null where the vehicle has not sold. */
  salePrice: number | null
  /** GST charged on the sale, if it is known independently of the price. */
  gstOnSale?: number | null
  /** ISO date of the sale. */
  saleDate?: string | null
}

/**
 * Work out the credit for one deal.
 *
 * `purchasePrice` is the gross amount paid to the seller.
 */
export function assessCredit(
  deal: Pick<Deal, 'gst_treatment' | 'seller_type' | 'created_at' | 'paid_at' | 'acquired_date'>,
  purchasePrice: number,
  sale: SaleFacts
): CreditAssessment {
  const treatment = treatmentOf(deal)
  const purchaseDate = deal.acquired_date || deal.paid_at || deal.created_at || null
  const gross = Number(purchasePrice) || 0

  if (treatment === 'no_gst') {
    return {
      treatment,
      notional: 0,
      claimable: 0,
      marginSchemeCredit: 0,
      g1Bas: sale.salePrice != null ? Number(sale.salePrice) : null,
      gstOnSale: sale.salePrice != null ? gstComponent(sale.salePrice) : null,
      capped: false,
      attributesTo: 'purchase',
      attributionDate: purchaseDate,
      note: 'No GST on this acquisition.',
    }
  }

  if (treatment === 'tax_invoice') {
    const credit = gstComponent(gross)
    return {
      treatment,
      notional: credit,
      claimable: credit,
      // A tax invoice is claimed in full, in the period of purchase - no cap applies.
      marginSchemeCredit: gross,
      g1Bas: sale.salePrice != null ? Number(sale.salePrice) : null,
      gstOnSale: sale.salePrice != null ? gstComponent(sale.salePrice) : null,
      capped: false,
      attributesTo: 'purchase',
      attributionDate: purchaseDate,
      note: 'GST-registered seller — full input tax credit, claimed in the period of purchase.',
    }
  }

  // notional_itc — the margin-scheme case.
  const notional = gstComponent(gross)

  if (sale.salePrice == null) {
    return {
      treatment,
      notional,
      claimable: 0,
      // Nothing claimable yet, so no gross to work it out from either.
      marginSchemeCredit: 0,
      // Not sold, so not part of Total Sales for any period.
      g1Bas: null,
      gstOnSale: null,
      capped: false,
      attributesTo: null,
      attributionDate: null,
      note: 'Still in stock — the notional credit is not claimable until the vehicle is sold.',
    }
  }

  const gstOnSale =
    sale.gstOnSale != null ? Number(sale.gstOnSale) : gstComponent(sale.salePrice)
  /*
   * Margin Scheme Credit and G1 (BAS) - Winnie's spec of 10 Sep 2026.
   *
   * These are two different numbers on the same row, and the spec says so in as many
   * words: a bug that wires them to one formula "would silently pass every other test
   * in this table except this one".
   *
   *   Margin Scheme Credit = min(purchase, sale)   the gross the credit works off,
   *                                                capped per the ATO motor-vehicle rule
   *   G1 (BAS)             = sale price            full and uncapped, Total Sales
   *
   * On a car bought at $45,000 and sold at $48,000 they read $45,000 and $48,000. Both
   * are correct; they answer different questions.
   *
   * This supersedes the 1 Sep rule that had G1 showing the gross *margin* (sale minus
   * purchase). That column has no equivalent in the new spec and is gone: the margin is
   * still visible as eleven times the net GST, and the net GST itself has not moved.
   *
   * `claimable` is one eleventh of Margin Scheme Credit by construction rather than a
   * second, parallel calculation, so the row cannot read Credit = X and Claimable != X/11.
   */
  const marginSchemeCredit = Math.min(gross, Math.max(0, Number(sale.salePrice) || 0))
  const g1Bas = sale.salePrice != null ? Number(sale.salePrice) : null
  /* One eleventh of the capped gross, not a second calculation of its own. Where the
     invoice states its own GST that figure is still what is reported at 1A; the cap
     below is the ATO's "lesser of" test, worked from the prices. */
  const claimable = gstComponent(marginSchemeCredit)
  const capped = claimable < notional

  return {
    treatment,
    notional,
    claimable,
    marginSchemeCredit,
    g1Bas,
    gstOnSale,
    capped,
    attributesTo: 'sale',
    attributionDate: sale.saleDate || null,
    note: capped
      ? 'Credit limited to the GST payable on the sale.'
      : 'Notional credit, claimed in the period the vehicle sold.',
  }
}
